import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function NotificationBell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const dropRef = useRef(null);

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count);
    } catch (_) { /* ignore */ }
  }, [user]);

  useEffect(() => {
    fetchCount();
    if (!user) return;
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [fetchCount, user]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      setItems(data);
    })();
  }, [open]);

  useEffect(() => {
    const onClick = (e) => { if (open && dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const goTo = async (n) => {
    if (!n.read_at) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    fetchCount();
    const p = n.payload || {};
    if (n.kind === "new_message" && p.thread_id) navigate(`/messages/${p.thread_id}`);
    else if (p.submission_id) navigate(`/missions/${p.mission_id}`);
    else if (p.mission_id) navigate(`/missions/${p.mission_id}`);
    else navigate("/notifications");
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setCount(0);
    setItems((its) => its.map((i) => ({ ...i, read_at: new Date().toISOString() })));
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        data-testid="notif-bell"
        aria-label="notifications"
      >
        🔔
        {count > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white" data-testid="notif-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="notif-dropdown">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">{t("notifications.title")}</p>
            <button onClick={markAll} className="text-[11px] font-medium text-indigo-700 hover:text-indigo-800" data-testid="notif-read-all">
              {t("notifications.read_all")}
            </button>
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <li className="px-3 py-6 text-center text-xs text-slate-500">{t("notifications.empty")}</li>
            )}
            {items.map((n) => (
              <li key={n.id}>
                <button
                  onClick={() => goTo(n)}
                  className={
                    "flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-50 " +
                    (n.read_at ? "" : "bg-indigo-50/40")
                  }
                  data-testid={`notif-item-${n.id}`}
                >
                  <span className="mt-1 text-lg">
                    {n.kind === "new_message" ? "💬" : n.kind === "submission_hired" ? "🎉" : n.kind === "new_submission" ? "📥" : n.kind === "submission_status_changed" ? "🔁" : "🔔"}
                  </span>
                  <div className="flex-1 text-xs">
                    <p className="text-slate-800">{t(`notifications.copy_${n.kind}`, { ...n.payload, defaultValue: n.kind })}</p>
                    <p className="mt-0.5 text-[10px] text-slate-500">{formatWhen(n.created_at)}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-slate-100 px-3 py-2 text-center">
            <Link to="/notifications" onClick={() => setOpen(false)} className="text-[11px] font-medium text-indigo-700 hover:text-indigo-800" data-testid="notif-see-all">
              {t("notifications.see_all")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
